import { Link, Outlet, useRouterState } from "@tanstack/react-router";
import { Toaster } from "@/components/ui/sonner";
import { Menu, X, ChevronUp, MapPin, Mail, Phone } from "lucide-react";
import { useState } from "react";
import { RegisterDialog } from "@/components/site/RegisterDialog";
import { EventProvider, useSelectedEvent } from "@/lib/event-context";
import { EventLogo } from "@/components/site/EventLogo";
import { getPastEvents, getCurrentEvent, getEventStatusRange } from "@/lib/events";

const navItems = [
  { to: "/", label: "Home" },
  { to: "/agenda", label: "Agenda" },
  { to: "/speakers", label: "Speakers" },
  { to: "/sponsors", label: "Sponsors" },
  { to: "/venue", label: "Venue" },
  { to: "/gallery", label: "Gallery" },
  { to: "/contact", label: "Contact" },
] as const;

function Nav() {
  const [open, setOpen] = useState(false);
  const { selectedEvent } = useSelectedEvent();
  // Hide the Register button when the selected event is a past event —
  // past conferences are not open for registration.
  const showRegister = !selectedEvent || getEventStatusRange(selectedEvent) !== "past";

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-background/90 backdrop-blur-md border-b border-border">
      <div className="mx-auto flex w-full min-w-0 max-w-7xl flex-col gap-3 px-4 py-3 sm:px-6 lg:flex-row lg:items-center lg:justify-between lg:gap-4 lg:py-0 lg:h-24">
        <div className="flex items-center justify-between gap-3 min-w-0">
          <Link to="/" className="flex items-center gap-3 shrink-0" onClick={() => setOpen(false)}>
            <img
              src="/assets/images/1logo.png"
              alt="Logo 1"
              className="h-12 w-auto object-contain md:h-14 lg:h-16"
            />
            {/* <img src="/assets/images/2logo.jpg" alt="Logo 2" className="h-12 w-auto object-contain md:h-14 lg:h-16" /> */}
          </Link>
          <div className="flex items-center gap-2 xl:hidden">
            {showRegister && (
              <RegisterDialog
                triggerSize="sm"
                triggerClassName="hidden bg-accent text-accent-foreground hover:bg-accent/90 sm:inline-flex"
                eventId={selectedEvent?.id}
                eventTitle={selectedEvent?.title}
              />
            )}
            <button
              className="p-2 -mr-2 text-foreground"
              onClick={() => setOpen((o) => !o)}
              aria-label="Toggle menu"
            >
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        <EventLogo className="w-full justify-center lg:w-auto lg:max-w-[160px] xl:max-w-xs lg:justify-start shrink-0" />

        <nav className="hidden items-center gap-4 xl:gap-5 text-sm font-medium xl:flex shrink-0">
          {navItems.map((i) => (
            <Link
              key={i.to}
              to={i.to}
              activeOptions={{ exact: true }}
              activeProps={{ className: "text-accent" }}
              className="text-foreground/80 hover:text-accent transition-colors uppercase tracking-wide whitespace-nowrap"
            >
              {i.label}
            </Link>
          ))}
        </nav>

        {showRegister ? (
          <RegisterDialog
            triggerSize="sm"
            triggerClassName="hidden bg-accent text-accent-foreground hover:bg-accent/90 xl:inline-flex shrink-0"
            eventId={selectedEvent?.id}
            eventTitle={selectedEvent?.title}
          />
        ) : (
          <div className="hidden xl:block shrink-0" aria-hidden="true" />
        )}
      </div>
      {open && (
        <div className="border-t border-border bg-background xl:hidden">
          <nav className="flex flex-col gap-1 px-4 py-4">
            {navItems.map((i) => (
              <Link
                key={i.to}
                to={i.to}
                activeOptions={{ exact: true }}
                activeProps={{ className: "text-accent bg-secondary" }}
                onClick={() => setOpen(false)}
                className="rounded-md px-3 py-3 text-sm font-medium uppercase tracking-wide text-foreground/80 hover:bg-secondary hover:text-accent"
              >
                {i.label}
              </Link>
            ))}
            {showRegister && (
              <RegisterDialog
                triggerClassName="mt-2 w-full bg-accent text-accent-foreground hover:bg-accent/90"
                eventId={selectedEvent?.id}
                eventTitle={selectedEvent?.title}
              />
            )}
          </nav>
        </div>
      )}
    </header>
  );
}

function Footer() {
  const { events, selectedEventId, selectEvent } = useSelectedEvent();
  const currentEvent = getCurrentEvent(events, selectedEventId);
  const allPastEvents = getPastEvents(events, currentEvent?.id);
  const pastEvents = allPastEvents.slice(0, 6);
  const hasMorePastEvents = allPastEvents.length > pastEvents.length;

  return (
    <footer className="bg-primary text-primary-foreground/80 pt-12 pb-8 px-4 sm:px-6 relative">
      <div className="max-w-7xl mx-auto grid gap-10 md:grid-cols-4 mb-10">
        {/* Col 1 — brand */}
        <div>
          <div className="font-serif text-xl text-primary-foreground mb-3">UASA Events Program</div>
          <p className="text-sm leading-relaxed">
            Conferences and gatherings bringing together leaders, regulators and innovators shaping
            the future of Arab capital markets across the region.
          </p>
        </div>

        {/* Col 2 — quick links */}
        <div>
          <div className="text-accent text-xs uppercase tracking-widest mb-4">Quick Links</div>
          <div className="flex flex-col gap-2 text-sm">
            {navItems.map((i) => (
              <Link key={i.to} to={i.to} className="hover:text-accent transition-colors w-fit">
                {i.label}
              </Link>
            ))}
          </div>
        </div>

        {/* Col 3 — past events */}
        <div>
          <div className="text-accent text-xs uppercase tracking-widest mb-4">Past Events</div>
          {pastEvents.length === 0 ? (
            <p className="text-sm text-primary-foreground/50">No past events yet.</p>
          ) : (
            <>
              <ul className="space-y-2 text-sm">
                {pastEvents.map((event) => (
                  <li key={event.id}>
                    <Link
                      to="/events/$eventId"
                      params={{ eventId: String(event.id) }}
                      onClick={() => selectEvent(event.id)}
                      className="hover:text-accent transition-colors line-clamp-1"
                      title={event.title}
                    >
                      {event.title}
                    </Link>
                  </li>
                ))}
              </ul>
              {hasMorePastEvents && (
                <Link
                  to="/past-events"
                  className="inline-flex items-center gap-1 mt-3 text-xs uppercase tracking-wide text-accent hover:text-accent/80 transition-colors"
                >
                  See more
                  <ChevronUp className="w-3.5 h-3.5 rotate-90" />
                </Link>
              )}
            </>
          )}
        </div>

        {/* Col 4 — contact */}
        <div>
          <div className="text-accent text-xs uppercase tracking-widest mb-4">Contact</div>
          <ul className="space-y-2 text-sm">
            <li className="flex items-start gap-2">
              <MapPin className="w-4 h-4 mt-0.5 text-accent shrink-0" /> Dubai, United Arab Emirates
            </li>
            <li className="flex items-start gap-2">
              <Mail className="w-4 h-4 mt-0.5 text-accent shrink-0" />
              <a href="mailto:info@uasa.ae" className="hover:text-accent transition-colors">info@uasa.ae</a>
            </li>
            <li className="flex items-start gap-2">
              <Phone className="w-4 h-4 mt-0.5 text-accent shrink-0" />
              <a href="tel:+97142900056" className="hover:text-accent transition-colors">+971 4 290 0056</a>
            </li>
          </ul>
        </div>
      </div>

      <div className="max-w-7xl mx-auto border-t border-accent/20 pt-6 text-center text-xs">
        © 2024 Union of Arab Securities Authorities. All rights reserved.
      </div>

      <button
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        className="fixed right-4 bottom-4 sm:right-6 sm:bottom-6 w-11 h-11 rounded-full bg-accent text-accent-foreground flex items-center justify-center hover:scale-110 transition-transform shadow-lg z-40"
        aria-label="Back to top"
      >
        <ChevronUp className="w-5 h-5" />
      </button>
    </footer>
  );
}

export function PageHero({
  eyebrow,
  title,
  subtitle,
}: {
  eyebrow?: string;
  title: string;
  subtitle?: string;
}) {
  return (
    <section
      className="relative pt-40 pb-12 sm:pt-44 sm:pb-16 md:pt-48 md:pb-24 px-4 sm:px-6 lg:px-8 text-center text-white"
      style={{
        background:
          "linear-gradient(135deg, oklch(0.25 0.08 270) 0%, oklch(0.32 0.1 250) 50%, oklch(0.4 0.11 220) 100%)",
      }}
    >
      {eyebrow && (
        <div className="text-[10px] sm:text-xs uppercase tracking-[0.25em] sm:tracking-[0.3em] text-accent mb-3 sm:mb-4">
          {eyebrow}
        </div>
      )}
      <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl max-w-4xl mx-auto leading-[1.1]">
        {title}
      </h1>
      {/* {subtitle && (
        <p className="mt-4 sm:mt-5 max-w-2xl mx-auto text-white/80 text-sm sm:text-base md:text-lg px-2">
          {subtitle}
        </p>
      )} */}
    </section>
  );
}

export function Layout() {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const isAdmin = pathname.startsWith("/admin");

  if (isAdmin) {
    return (
      <>
        <Outlet />
        <Toaster />
      </>
    );
  }

  return (
    <EventProvider>
      <div className="min-h-screen flex flex-col bg-background font-sans overflow-x-hidden">
        <Nav />
        <main className="flex-1 min-w-0">
          <Outlet />
        </main>
        <Footer />
        <Toaster />
      </div>
    </EventProvider>
  );
}
